import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import QMSLayout from "../components/QMSLayout";
import { useAuth } from "../App";
import { CheckCircle2, Circle, AlertTriangle, ChevronDown, ChevronRight, FileText, Shield, Layers, Users, ClipboardCheck, BarChart3, Upload, Send, MessageSquare, X, Loader2, Clock, XCircle, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  quality_management: { label: "Quality Management", icon: Shield, color: "#36A9E1" },
  risk_management: { label: "Risk Management", icon: AlertTriangle, color: "#FBBF24" },
  regulatory: { label: "Regulatory", icon: FileText, color: "#F87171" },
  supplier_quality: { label: "Supplier Quality", icon: Users, color: "#4ADE80" },
  corrective_actions: { label: "CAPA", icon: ClipboardCheck, color: "#A78BFA" },
  audits: { label: "Audits", icon: BarChart3, color: "#38BDF8" },
  training: { label: "Training", icon: Layers, color: "#FB923C" },
  software_lifecycle: { label: "Software Lifecycle", icon: FileText, color: "#36A9E1" },
  cybersecurity: { label: "Cybersecurity", icon: Shield, color: "#F87171" },
  data_integrity: { label: "Data Integrity", icon: Shield, color: "#FBBF24" },
  usability: { label: "Usability", icon: Users, color: "#4ADE80" },
  general: { label: "General", icon: ClipboardCheck, color: "#8B95A5" }
};

const STATUS_BADGE = {
  draft: { bg: "bg-slate-400/10", text: "text-slate-400", label: "Draft" },
  submitted: { bg: "bg-blue-400/10", text: "text-blue-400", label: "Submitted" },
  needs_revision: { bg: "bg-amber-400/10", text: "text-amber-400", label: "Needs Revision" },
  accepted: { bg: "bg-emerald-400/10", text: "text-emerald-400", label: "Accepted" },
  rejected: { bg: "bg-red-400/10", text: "text-red-400", label: "Rejected" },
  no_response: { bg: "bg-slate-500/10", text: "text-slate-500", label: "Not Started" }
};

export default function ClientOnboardingDashboardPage() {
  const { t } = useTranslation("operations");
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCats, setExpandedCats] = useState({});
  const [activeItem, setActiveItem] = useState(null);
  const [view, setView] = useState("dashboard");

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/client-onboarding/my-runs`, { credentials: "include" });
      const data = await res.json();
      setRuns(data.runs || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const openRun = async (runId) => {
    const res = await fetch(`${API}/api/client-onboarding/runs/${runId}`, { credentials: "include" });
    const data = await res.json();
    setRunDetail(data);
    setSelectedRun(runId);
    setView("run");
    const cats = {};
    (data.items || []).forEach(i => { cats[i.category] = true; });
    setExpandedCats(cats);

    const sumRes = await fetch(`${API}/api/client-onboarding/runs/${runId}/summary`, { credentials: "include" });
    setSummary(await sumRes.json());
  };

  const refreshRun = () => selectedRun && openRun(selectedRun);

  const submitResponse = async (itemId, text, status = "submitted") => {
    await fetch(`${API}/api/client-onboarding/runs/${selectedRun}/responses`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ item_id: itemId, response_text: text, status })
    });
    refreshRun();
  };

  const addComment = async (itemId, comment) => {
    await fetch(`${API}/api/client-onboarding/runs/${selectedRun}/comments`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ item_id: itemId, comment })
    });
    refreshRun();
  };

  const uploadArtifact = async (itemId, file) => {
    const formData = new FormData();
    formData.append("item_id", itemId);
    formData.append("artifact_type", "document");
    formData.append("file", file);
    await fetch(`${API}/api/client-onboarding/runs/${selectedRun}/artifacts`, {
      method: "POST",
      credentials: "include",
      body: formData
    });
    refreshRun();
  };

  const toggleCat = (cat) => setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  const groupItems = (items) => {
    const groups = {};
    (items || []).forEach(item => {
      const cat = item.category || "general";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  };

  const getItemStatus = (itemId) => {
    if (!runDetail?.responses) return "no_response";
    const resp = runDetail.responses[itemId];
    return resp ? resp.status : "no_response";
  };

  const getItemResponse = (itemId) => {
    if (!runDetail?.responses) return null;
    return runDetail.responses[itemId] || null;
  };

  const getItemComments = (itemId) => {
    return (runDetail?.comments || []).filter(c => c.item_id === itemId);
  };

  const getItemArtifacts = (itemId) => {
    return (runDetail?.artifacts || []).filter(a => a.item_id === itemId);
  };

  return (
    <QMSLayout title="Client Onboarding Portal">
      <div className="space-y-6" data-testid="client-onboarding-dashboard">

        {/* DASHBOARD VIEW */}
        {view === "dashboard" && (
          <>
            <div>
              <p className="text-[#8B95A5]" data-testid="portal-subtitle">Your onboarding checklists and submission status</p>
            </div>

            {loading && <div className="text-center py-12 text-[#8B95A5]">{t("common:status.loading")}</div>}

            {!loading && runs.length === 0 && (
              <div className="text-center py-16 text-[#8B95A5]" data-testid="no-runs">
                <ClipboardCheck size={48} className="mx-auto mb-4 opacity-30" />
                <p>No onboarding checklists assigned yet.</p>
                <p className="text-sm mt-1">Contact your account manager to get started.</p>
              </div>
            )}

            {!loading && runs.map(run => (
              <div
                key={run.run_id}
                onClick={() => openRun(run.run_id)}
                className="qms-card hover:border-[rgba(255,255,255,0.15)] transition-colors cursor-pointer"
                data-testid={`client-run-${run.run_id}`}
              >
                <div className="qms-card-body flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-[#38BDF8] font-mono text-xs">{run.run_id}</span>
                      <span className={`badge ${run.status === "ready" ? "badge-green" : run.status === "completed" ? "badge-blue" : "badge-yellow"}`}>
                        {run.status.replace("_", " ")}
                      </span>
                    </div>
                    <h3 className="text-white font-semibold text-lg">{run.template_name}</h3>
                    <p className="text-[#8B95A5] text-sm">{run.client_name}</p>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">{run.progress_pct}%</p>
                      <p className="text-xs text-[#8B95A5]">{run.completed_items}/{run.total_items} items</p>
                    </div>
                    <div className="w-28 h-2 bg-[#0D0F14] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${run.progress_pct === 100 ? "bg-[#4ADE80]" : "bg-[#36A9E1]"}`} style={{ width: `${run.progress_pct}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* RUN DETAIL VIEW */}
        {view === "run" && runDetail && (
          <>
            <div className="flex items-center justify-between">
              <div>
                <button onClick={() => { setView("dashboard"); setRunDetail(null); setSelectedRun(null); }} className="text-sm text-[#8B95A5] hover:text-white mb-2 flex items-center gap-1" data-testid="back-to-dashboard">
                  ← Back to Dashboard
                </button>
                <h2 className="text-white font-['Syne'] text-xl font-bold">{runDetail.template_name}</h2>
                <p className="text-[#8B95A5] text-sm">{runDetail.client_name} · {runDetail.run_id}</p>
              </div>
              <div className="flex items-center gap-4">
                {summary && (
                  <div className="text-right">
                    <p className="text-2xl font-bold text-white">{summary.readiness_pct}%</p>
                    <p className="text-xs text-[#8B95A5]">{summary.accepted_items}/{summary.total_items} accepted</p>
                  </div>
                )}
              </div>
            </div>

            {/* Progress summary bar */}
            {summary && (
              <div className="qms-card">
                <div className="qms-card-body">
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-emerald-400" /> Accepted: {summary.accepted_items}</div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-400" /> Submitted: {summary.submitted_items}</div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-amber-400" /> Needs Revision: {summary.needs_revision_items}</div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-slate-400" /> Draft: {summary.draft_items}</div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-slate-600" /> Not Started: {summary.no_response_items}</div>
                  </div>
                  <div className="mt-3 w-full h-3 bg-[#0D0F14] rounded-full overflow-hidden flex">
                    {summary.total_items > 0 && (
                      <>
                        <div className="h-full bg-emerald-400" style={{ width: `${(summary.accepted_items / summary.total_items) * 100}%` }} />
                        <div className="h-full bg-blue-400" style={{ width: `${(summary.submitted_items / summary.total_items) * 100}%` }} />
                        <div className="h-full bg-amber-400" style={{ width: `${(summary.needs_revision_items / summary.total_items) * 100}%` }} />
                        <div className="h-full bg-slate-400" style={{ width: `${(summary.draft_items / summary.total_items) * 100}%` }} />
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Items grouped by category */}
            {Object.entries(groupItems(runDetail.items)).map(([cat, items]) => {
              const meta = CATEGORY_META[cat] || CATEGORY_META.general;
              const Icon = meta.icon;
              const accepted = items.filter(i => getItemStatus(i.item_id) === "accepted").length;
              const expanded = expandedCats[cat] !== false;

              return (
                <div key={cat} className="qms-card" data-testid={`client-category-${cat}`}>
                  <div className="qms-card-body">
                    <button onClick={() => toggleCat(cat)} className="w-full flex items-center justify-between py-1">
                      <div className="flex items-center gap-3">
                        <Icon size={18} style={{ color: meta.color }} />
                        <span className="text-white font-semibold">{meta.label}</span>
                        <span className="text-xs text-[#8B95A5]">({accepted}/{items.length})</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-20 h-1.5 bg-[#0D0F14] rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${items.length ? (accepted / items.length * 100) : 0}%`, background: meta.color }} />
                        </div>
                        {expanded ? <ChevronDown size={16} className="text-[#8B95A5]" /> : <ChevronRight size={16} className="text-[#8B95A5]" />}
                      </div>
                    </button>

                    {expanded && (
                      <div className="mt-3 space-y-2">
                        {items.sort((a, b) => a.order - b.order).map(item => {
                          const status = getItemStatus(item.item_id);
                          const resp = getItemResponse(item.item_id);
                          const comments = getItemComments(item.item_id);
                          const artifacts = getItemArtifacts(item.item_id);
                          const badge = STATUS_BADGE[status] || STATUS_BADGE.no_response;
                          const isActive = activeItem === item.item_id;

                          return (
                            <div key={item.item_id} className={`p-3 rounded border transition-colors ${status === "accepted" ? "border-[rgba(74,222,128,0.2)] bg-[rgba(74,222,128,0.03)]" : status === "needs_revision" ? "border-[rgba(251,191,36,0.2)] bg-[rgba(251,191,36,0.03)]" : "border-[rgba(255,255,255,0.05)] bg-[rgba(255,255,255,0.02)]"}`} data-testid={`client-item-${item.item_id}`}>
                              <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={() => setActiveItem(isActive ? null : item.item_id)}>
                                <div className="flex items-start gap-3 flex-1">
                                  {status === "accepted" ? <CheckCircle2 size={20} className="text-[#4ADE80] mt-0.5 shrink-0" /> :
                                   status === "needs_revision" ? <RefreshCw size={20} className="text-[#FBBF24] mt-0.5 shrink-0" /> :
                                   status === "submitted" ? <Clock size={20} className="text-[#38BDF8] mt-0.5 shrink-0" /> :
                                   <Circle size={20} className="text-[#8B95A5] mt-0.5 shrink-0" />}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                      <span className={`font-medium text-sm ${status === "accepted" ? "text-[#4ADE80]" : "text-white"}`}>{item.title}</span>
                                      {item.required && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F87171]/10 text-[#F87171]">Required</span>}
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${badge.bg} ${badge.text}`}>{badge.label}</span>
                                    </div>
                                    {item.description && <p className="text-xs text-[#8B95A5] mb-1">{item.description}</p>}
                                    {item.standard_refs?.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mb-1">
                                        {item.standard_refs.map(ref => (
                                          <span key={ref} className="text-[10px] px-1.5 py-0.5 rounded bg-[#36A9E1]/10 text-[#36A9E1] font-mono">{ref}</span>
                                        ))}
                                      </div>
                                    )}
                                    {item.expected_artifacts?.length > 0 && (
                                      <p className="text-xs text-[#8B95A5]"><span className="font-medium">Expected: </span>{item.expected_artifacts.join(", ")}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  {artifacts.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#36A9E1]/10 text-[#36A9E1]">{artifacts.length} files</span>}
                                  {comments.length > 0 && <MessageSquare size={14} className="text-[#8B95A5]" />}
                                  {isActive ? <ChevronDown size={16} className="text-[#8B95A5]" /> : <ChevronRight size={16} className="text-[#8B95A5]" />}
                                </div>
                              </div>

                              {/* Expanded item detail */}
                              {isActive && (
                                <ItemDetailPanel
                                  item={item}
                                  response={resp}
                                  comments={comments}
                                  artifacts={artifacts}
                                  status={status}
                                  onSubmitResponse={(text, st) => submitResponse(item.item_id, text, st)}
                                  onAddComment={(text) => addComment(item.item_id, text)}
                                  onUploadArtifact={(file) => uploadArtifact(item.item_id, file)}
                                  readOnly={status === "accepted"}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </QMSLayout>
  );
}

function ItemDetailPanel({ item, response, comments, artifacts, status, onSubmitResponse, onAddComment, onUploadArtifact, readOnly }) {
  const [text, setText] = useState(response?.response_text || "");
  const [commentText, setCommentText] = useState("");
  const [saving, setSaving] = useState(false);
  const fileInputRef = React.useRef();

  const handleSave = async (submitStatus) => {
    setSaving(true);
    await onSubmitResponse(text, submitStatus);
    setSaving(false);
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    await onAddComment(commentText);
    setCommentText("");
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) await onUploadArtifact(file);
  };

  return (
    <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.05)] space-y-3" data-testid={`item-detail-${item.item_id}`}>
      {/* Response input */}
      <div>
        <label className="text-xs text-[#8B95A5] block mb-1">Your Response</label>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Describe how your organization meets this requirement..."
          rows={3}
          disabled={readOnly}
          className="w-full bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] rounded px-3 py-2 text-sm text-white placeholder-[#555] resize-none outline-none focus:border-[#36A9E1]/40 disabled:opacity-50"
          data-testid={`response-text-${item.item_id}`}
        />
        {!readOnly && (
          <div className="flex gap-2 mt-2">
            <button onClick={() => handleSave("draft")} disabled={saving || !text} className="text-xs px-3 py-1.5 rounded bg-[#1F222D] text-[#8B95A5] hover:text-white disabled:opacity-40" data-testid={`save-draft-${item.item_id}`}>
              {saving ? <Loader2 size={12} className="animate-spin" /> : "Save Draft"}
            </button>
            <button onClick={() => handleSave("submitted")} disabled={saving || !text} className="text-xs px-3 py-1.5 rounded bg-[#36A9E1]/20 text-[#36A9E1] hover:bg-[#36A9E1]/30 disabled:opacity-40 flex items-center gap-1" data-testid={`submit-response-${item.item_id}`}>
              <Send size={12} /> Submit
            </button>
            <button onClick={() => fileInputRef.current?.click()} className="text-xs px-3 py-1.5 rounded bg-[#1F222D] text-[#8B95A5] hover:text-white flex items-center gap-1 ml-auto" data-testid={`upload-btn-${item.item_id}`}>
              <Upload size={12} /> Upload Artifact
            </button>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect} />
          </div>
        )}
      </div>

      {/* Artifacts */}
      {artifacts.length > 0 && (
        <div>
          <label className="text-xs text-[#8B95A5] block mb-1">Uploaded Artifacts</label>
          <div className="space-y-1">
            {artifacts.map(a => {
              const aBadge = STATUS_BADGE[a.status] || STATUS_BADGE.submitted;
              return (
                <div key={a.artifact_id} className="flex items-center gap-2 text-xs p-2 rounded bg-[#0D0F14]">
                  <FileText size={14} className="text-[#36A9E1]" />
                  <span className="text-white">{a.file_name}</span>
                  <span className={`ml-auto px-1.5 py-0.5 rounded ${aBadge.bg} ${aBadge.text}`}>{aBadge.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Comments thread */}
      {comments.length > 0 && (
        <div>
          <label className="text-xs text-[#8B95A5] block mb-1">Comments</label>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {comments.map(c => (
              <div key={c.comment_id} className={`text-xs p-2 rounded ${c.actor_type === "client" ? "bg-[#36A9E1]/5 border-l-2 border-[#36A9E1]" : "bg-[#FBBF24]/5 border-l-2 border-[#FBBF24]"}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-white">{c.actor_name}</span>
                  <span className="text-[#555]">{c.actor_type === "client" ? "Client" : "Reviewer"}</span>
                  <span className="text-[#555] ml-auto">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-[#CBD5E1]">{c.comment}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add comment */}
      {!readOnly && (
        <div className="flex gap-2">
          <input
            value={commentText}
            onChange={e => setCommentText(e.target.value)}
            placeholder="Add a comment..."
            className="flex-1 bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] rounded px-2 py-1.5 text-xs text-white placeholder-[#555] outline-none focus:border-[#36A9E1]/40"
            onKeyDown={e => e.key === "Enter" && handleComment()}
            data-testid={`comment-input-${item.item_id}`}
          />
          <button onClick={handleComment} disabled={!commentText.trim()} className="text-xs px-2 py-1.5 rounded bg-[#1F222D] text-[#8B95A5] hover:text-white disabled:opacity-40" data-testid={`send-comment-${item.item_id}`}>
            <MessageSquare size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
